import { Skeleton } from '@avanta/shared/components/ui/Skeleton';
import { type ReactElement } from 'react';

const WEEKDAY_HEADERS = 7;
const CALENDAR_ROWS = 5;
const DAYS_PER_WEEK = 7;
const DAYTIME_SLOTS_COUNT = 13;
const EMOTIONAL_SUPPORT_SLOTS_COUNT = 11;

const CalendarSkeleton = (): ReactElement => (
  <div className="w-full rounded-xl border border-teal-300 p-3 px-2 am-lg:border-transparent am-lg:p-0">
    <div className="flex flex-col gap-4 px-3">
      <div className="relative flex h-8 items-center justify-center">
        <Skeleton className="absolute left-0 size-8 rounded-md" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="absolute right-0 size-8 rounded-md" />
      </div>
      <div className="flex flex-col">
        <div className="flex">
          {Array.from({ length: WEEKDAY_HEADERS }).map((_, index) => (
            <div key={`weekday-${index}`} className="flex flex-1 justify-center">
              <Skeleton className="h-4 w-6" />
            </div>
          ))}
        </div>
        {Array.from({ length: CALENDAR_ROWS }).map((_, rowIndex) => (
          <div key={`week-${rowIndex}`} className="mt-2 flex w-full">
            {Array.from({ length: DAYS_PER_WEEK }).map((_, dayIndex) => (
              <div
                key={`day-${rowIndex}-${dayIndex}`}
                className="flex flex-1 items-center justify-center"
              >
                <Skeleton className="size-9 rounded-full" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface SlotsGridSkeletonProps {
  slotsCount: number;
  showSubtitle?: boolean;
  showTooltip?: boolean;
}

const SlotsGridSkeleton = ({
  slotsCount,
  showSubtitle = false,
  showTooltip = false,
}: SlotsGridSkeletonProps): ReactElement => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <div>
        <Skeleton className="h-4 w-24" />
        {showSubtitle ? <Skeleton className="mt-1 h-4 w-40" /> : null}
      </div>
      {showTooltip ? <Skeleton className="size-4 rounded-full" /> : null}
    </div>
    <div className="grid grid-cols-5 gap-2">
      {Array.from({ length: slotsCount }).map((_, index) => (
        <Skeleton key={`slot-${index}`} className="h-8 w-full rounded" />
      ))}
    </div>
  </div>
);

export const DateTimeStepSkeleton = (): ReactElement => (
  <div className="flex flex-col gap-8 am-lg:gap-10">
    <div className="flex flex-col gap-2 am-lg:gap-4">
      <Skeleton className="h-5 w-56" />
      <div className="grid gap-4 rounded-xl border border-transparent p-0 am-lg:grid-cols-2 am-lg:gap-12 am-lg:border-teal-300 am-lg:p-6">
        <CalendarSkeleton />
        <div className="flex flex-col gap-5">
          <SlotsGridSkeleton slotsCount={DAYTIME_SLOTS_COUNT} />
          <Skeleton className="h-px w-full" />
          <SlotsGridSkeleton slotsCount={EMOTIONAL_SUPPORT_SLOTS_COUNT} showSubtitle showTooltip />
        </div>
      </div>
    </div>
  </div>
);
